import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  CellFunction,
  RESPONSE_NA,
  RESPONSE_NONE
} from '../../cells';
import {
  IEpxOpioidMgmt,
  Modules,
  PainOptions
} from '../../../../../libs/apps/epxopioidmgmt';

const PainCell: CellFunction<IEpxOpioidMgmt> = ({
  row: { original: session }
}) => {
  if (session.module === 'pmt') {
    return <span>{session._pmt.ratePain || 'N/A'}</span>;
  } else {
    return RESPONSE_NA;
  }
};

const PillsCell: CellFunction<IEpxOpioidMgmt> = ({
  row: { original: session }
}) => {
  if (session.module === 'pmt') {
    const { _pmt } = session;
    if (_pmt.numberPills) {
      return <span>{_pmt.numberPills}</span>;
    } else {
      const label = _pmt.aboveThreshold ? 'Above Threshold' : 'Below Threshold';
      return <span>{label}</span>;
    }
  } else {
    return RESPONSE_NA;
  }
};

const ControlCell: CellFunction<IEpxOpioidMgmt> = ({
  row: { original: session }
}) => {
  if (session.module === 'pmt') {
    const { _pmt } = session;
    if (_pmt.painOptions && _pmt.painOptions.length) {
      if (_pmt.painOptions.indexOf(7) !== -1) {
        return <span>None</span>;
      } else {
        const options: JSX.Element[] = [];
        _pmt.painOptions.forEach((po, index) => {
          options.push(
            <React.Fragment key={`${PainOptions[po]}-${index}}`}>
              {PainOptions[po]}
              <br />
            </React.Fragment>
          );
        });
        return <span>{options}</span>;
      }
    } else {
      return RESPONSE_NONE;
    }
  } else {
    return RESPONSE_NA;
  }
};

const ExtraCellContent = ({
  label,
  value,
  isLast
}: {
  label: string;
  value: number;
  isLast?: boolean;
}) => {
  let content: number | string = '?';
  if (value && value >= 0) {
    content = value;
  }
  return (
    <React.Fragment>
      <span>
        {label}: <b>{content}</b>
      </span>
      {isLast ? null : <br />}
    </React.Fragment>
  );
};

const ExtraCellYesNoContent = ({
  label,
  value,
  isLast
}: {
  label: string;
  value: number | boolean;
  isLast?: boolean;
}) => {
  let content = '?';
  if (typeof value === 'number') {
    if (value === 0) {
      content = 'No';
    } else {
      content = 'Yes';
    }
  }
  if (typeof value === 'boolean') {
    if (!value) {
      content = 'No';
    } else {
      content = 'Yes';
    }
  }
  return (
    <React.Fragment>
      <span>
        {label}: <b>{content}</b>
      </span>
      {isLast ? null : <br />}
    </React.Fragment>
  );
};

const ExtraCell: CellFunction<IEpxOpioidMgmt> = ({
  row: { original: session }
}) => {
  if (session.module === 'mood' && session._mood) {
    const { _mood } = session;
    const parts = [];
    parts.push(
      <ExtraCellContent
        key={`session-${session.id}-moodrating-${_mood.moodRating}`}
        label="Mood"
        value={_mood.moodRating}
      />
    );
    parts.push(
      <ExtraCellContent
        key={`session-${session.id}-recentinterest-${_mood.recentInterest}`}
        label="PHQ2 (Interest)"
        value={_mood.recentInterest}
      />
    );
    parts.push(
      <ExtraCellContent
        key={`session-${session.id}-recentdepressed-${_mood.recentDepressed}`}
        label="PHQ2 (Down)"
        value={_mood.recentDepressed}
        isLast
      />
    );
    return <div>{parts}</div>;
  } else if (session.module === 'substance' && session._substance) {
    const { _substance } = session;
    const parts = [];
    if (_substance.hasUsed === null) {
      parts.push(
        <ExtraCellYesNoContent
          key={`session-${session.id}-hasused-${_substance.hasUsed}`}
          label="Used Recently"
          value={null}
        />
      );
      parts.push(
        <ExtraCellContent
          key={`session-${session.id}-dayssinceuse-${_substance.daysSinceUse}`}
          label="Days Since Use"
          value={null}
          isLast
        />
      );
    } else if (_substance.hasUsed === true) {
      parts.push(
        <ExtraCellYesNoContent
          key={`session-${session.id}-hasused-${_substance.hasUsed}`}
          label="Used Recently"
          value={_substance.hasUsed}
        />
      );
      parts.push(
        <ExtraCellContent
          key={`session-${session.id}-dayssinceuse-${_substance.daysSinceUse}`}
          label="Days Since Use"
          value={_substance.daysSinceUse}
          isLast
        />
      );
    } else if (_substance.hasUsed === false) {
      parts.push(
        <ExtraCellYesNoContent
          key={`session-${session.id}-urgetouserating-${_substance.urgeToUseRating}`}
          label="Urge to Use"
          value={_substance.urgeToUseRating}
          isLast
        />
      );
    }
    return <div>{parts}</div>;
  } else {
    return RESPONSE_NA;
  }
};

const EpxOpioidMgmtTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxOpioidMgmt>[] = [
    {
      Header: 'Module',
      id: 'module',
      Cell: ResponseGuard(
        MappingCell('module', Modules) as CellFunction<IEpxOpioidMgmt>
      )
    },
    {
      Header: 'Pain (0-9)',
      id: 'ratePain',
      Cell: ResponseGuard(PainCell)
    },
    {
      Header: 'Pills Count',
      id: 'numberPills',
      Cell: ResponseGuard(PillsCell)
    },
    {
      Header: 'Pain Control',
      id: 'painOptions',
      Cell: ResponseGuard(ControlCell)
    },
    {
      Header: 'Extra Data',
      id: '_extras',
      Cell: ExtraCell
    }
  ];
  return (
    <SessionTable<IEpxOpioidMgmt>
      url="/epxopioidmgmt/data/"
      columns={columns}
      {...props}
    />
  );
};

export default EpxOpioidMgmtTable;
